import { Directive, inject, OnInit } from '@angular/core';
import { logger } from '@core/helpers/logger';
import { AccountService, ContextService } from '@core/services';
import { environment } from '@env/environment';
import { ChangableComponent } from '@models/changable.component';
import { StepByStepWizardSteps } from '@shared/step-by-step-wizard';
import { CCurrencyAmount, CurrencyEnum, User, UserRoleEnum } from 'lingo2-models';
import { OnUiButtonState } from 'onclass-ui';
import { of, takeUntil } from 'rxjs';
import { distinctUntilChanged, mergeAll, tap } from 'rxjs/operators';
import { PaymentConfiguration, PaymentFail, PaymentOptions, PaymentWidgetService } from '../components/payment-widget';

@Directive()
export abstract class AbstractCheckoutWizard<ContextType, WizardViewType extends string>
  extends ChangableComponent
  implements OnInit
{
  // DI
  protected readonly paymentWidgetService = inject(PaymentWidgetService);
  protected readonly contextService = inject(ContextService);

  public steps = new StepByStepWizardSteps<ContextType>([]);
  public activeView: WizardViewType;
  public paymentOptions: PaymentOptions;
  public paymentConfigured = false;
  public paymentProcessing = false;
  public payButtonsState: OnUiButtonState = 'default';
  public paymentProcessingError: string;
  public errorMessage: string;
  public amount = new CCurrencyAmount({
    currency_id: CurrencyEnum.usd,
    amount: 0,
  });
  public totalAmount = new CCurrencyAmount({
    currency_id: CurrencyEnum.usd,
    amount: 0,
  });
  public debug: boolean;

  protected _me: User;

  public ngOnInit() {
    this.initWizardSteps();
    this.setupWizardSteps();

    of(...[this.watchMe$, this.watchDebug$, this.watchStepChanged$, this.watchPayAmountChanged$])
      .pipe(
        mergeAll(),
        tap(() => this.detectChanges()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get me(): User {
    return this._me;
  }

  /** Можно ли начать исполнение платежа */
  public canExecutePayment(): boolean {
    return (
      this.payButtonsState !== 'progress' &&
      this.paymentConfigured &&
      !this.paymentProcessingError &&
      !this.errorMessage
    );
  }

  /** Исполнение платежа */
  public onStartPayment() {
    if (!this.canExecutePayment()) {
      const agreement = document.getElementsByClassName('agreement')[0];
      if (agreement) {
        agreement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const widget = document.querySelector('app-payment-widget');
      if (widget) {
        widget.scrollTop = 0;
      }
      return;
    }
    this.paymentWidgetService.pay();
  }

  public onRetryPayment() {
    this.payButtonsState = 'default';
    this.detectChanges();

    this.onStartPayment();
  }

  public onPaymentProcessing(value: boolean) {
    this.paymentProcessing = value;
    if (this.paymentProcessing) {
      this.payButtonsState = 'progress';
    }
    this.detectChanges();
  }

  /** Обработчик на изменение настроек платёжной формы и параметров проведения платежей */
  public onPaymentConfigured(configuration: PaymentConfiguration) {
    this.paymentConfigured = configuration.complete;
    this.detectChanges();
  }

  /** Обработчик на исполнение платежа */
  public onPaymentSucceed() {
    this.payButtonsState = 'done';
    this.paymentWidgetService.succeedPayment();
    this.detectChanges();
    // this.setTimeout(() => {
    //   this.payButtonsState = 'default';
    //   this.detectChanges();
    // }, 2500);
  }

  /** Обработчик на прекращение платежа по желанию пользователя */
  // public onPaymentCanceled() {}

  /** Обработчик на прекращение платежа по техническим причинам */
  public onPaymentFailed(err: PaymentFail) {
    this.paymentProcessingError = err.message;
    this.payButtonsState = 'fail';
    this.detectChanges();
    // this.setTimeout(() => {
    //   this.payButtonsState = 'default';
    //   this.detectChanges();
    // }, 2500);
  }

  /**
   * Настройка данных для платежной формы
   * Метод должен настроить this.paymentOptions
   *
   * @see paymentOptions
   */
  public abstract setupPaymentWidget();

  /**
   * Инициализация шагов мастера
   * Метод должен настроить this.steps
   *
   * @see steps
   */
  protected abstract initWizardSteps();

  /** Настройка шагов мастера под набор входных параметров */
  protected abstract setupWizardSteps();

  /** Выполнить обработку после активации шага мастера */
  protected abstract onStepActivated(step: string);

  protected activateView(view: WizardViewType) {
    this.activeView = view;
    this.detectChanges();
  }

  protected get watchMe$() {
    return this.contextService.me$.pipe(tap((me) => (this._me = me)));
  }

  protected get watchDebug$() {
    return this.contextService.debug$.pipe(tap((debug) => (this.debug = debug)));
  }

  protected get watchStepChanged$() {
    return this.steps.activeStep$.pipe(
      distinctUntilChanged(),
      tap((step) => {
        this.onStepActivated(step);
        this.activateView(step as WizardViewType);
      }),
    );
  }

  protected get watchPayAmountChanged$() {
    return this.paymentWidgetService.payAmount$.pipe(tap((totalAmount) => (this.totalAmount = totalAmount)));
  }

  protected log(...message) {
    if (environment.env === 'dev' || AccountService.hasRole(this._me, UserRoleEnum.developer) || this.debug) {
      logger.debug(...message);
    }
  }
}
